import { isRole, normalizeRoles, ROLE } from './roles';
import { buildInheritanceMap } from './utils';

import type { AccessArgs, PayloadRequest, TypedUser, Where } from 'payload';
import type { Role } from './roles';

const RoleInheritance = buildInheritanceMap();

/**
 * True when the user holds one of `allowed`, or any role that inherits one — `[ROLE.DEV]` lets an
 * `admin` through. Name the lowest role a gate admits; naming one above it adds nothing.
 */
export const userHasRole = (user: PayloadRequest['user'], allowed: Role | Role[]): boolean => {
  const role = user?.role;
  if (!isRole(role)) return false;

  const inheritedRoles = RoleInheritance[role];
  return normalizeRoles(allowed).some((candidate) => inheritedRoles.includes(candidate));
};

/** Every role `userHasRole` admits for `allowed`, inheritance included. For a message that lists them, never for a gate. */
export const rolesAdmitting = (allowed: Role | Role[]): Role[] =>
  Object.values(ROLE).filter((role) => normalizeRoles(allowed).some((candidate) => RoleInheritance[role].includes(candidate)));

export const hasRole =
  (allowed: Role | Role[]) =>
  ({ req }: AccessArgs) =>
    userHasRole(req.user, allowed);

export const hiddenForNonRoles =
  (roles: Role | Role[]) =>
  ({ user }: { user: TypedUser }): boolean =>
    !userHasRole(user, roles);

/**
 * Nobody grants a role they do not reach, so a Developer cannot mint an Admin to sign in as.
 *
 * Admin is named separately because nothing inherits `api-read`, so the reach rule alone would leave
 * that role ungrantable by anyone. No assigner means a system write — the seeder, Google sign-up, or
 * the first-user bootstrap — none of which takes a role from a caller.
 */
export const canAssignRole = (assigner: PayloadRequest['user'], role: Role): boolean =>
  !assigner || userHasRole(assigner, [ROLE.ADMIN]) || userHasRole(assigner, role);

export const TRUSTED_LOG_CONTEXT = 'trustedLogWrite';

/** Staff, or the server writing its own log row through the trusted context. */
export const devOrTrustedLogWrite = ({ req }: AccessArgs): boolean =>
  userHasRole(req.user, [ROLE.DEV]) || (Boolean(req.user) && req.context?.[TRUSTED_LOG_CONTEXT] === true);

/** As {@link devOrTrustedLogWrite}, but a trusted write is scoped to the writer's own rows. */
export const devOrOwnTrustedLogWrite = ({ req }: AccessArgs): boolean | Where => {
  if (userHasRole(req.user, [ROLE.DEV])) return true;
  if (req.user && req.context?.[TRUSTED_LOG_CONTEXT] === true) {
    return { user: { equals: req.user.id } };
  }
  return false;
};

/**
 * Where `admin` and `developer` part company on reads: an Admin reads every MCP permission profile,
 * a Developer only their own. The writes on that collection part company too, and say so flatly with
 * `hasRole([ROLE.ADMIN])` in `src/mcp/config/index.ts` — a row bounds an agent, so minting one is an
 * administrator's act.
 */
export const ownApiKeysOrAdmin = ({ req: { user } }: AccessArgs): boolean | Where => {
  if (!user) return false;
  if (userHasRole(user, [ROLE.ADMIN])) return true;
  if (!userHasRole(user, [ROLE.DEV])) return false;
  return { user: { equals: user.id } };
};
